import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, RefreshCw } from 'lucide-react';
import AdminSidebar from '@/pages/admin/components/AdminSidebar';
import AdminTopBar from '@/pages/admin/components/AdminTopBar';
import PageHeader from '@/components/feature/PageHeader';
import ServicesTable from './components/ServicesTable';
import ServiceFormModal from './components/ServiceFormModal';
import BookingFeeSection from './components/BookingFeeSection';
import { useServices } from '@/hooks/useServices';
import { theme } from '@/lib/theme';
import type { AdminService } from '@/types';
import type { ServiceFormData } from '@/hooks/useServices';

export default function ServicesPage() {
  const { services, loading, error, saving, refetch, createService, updateService, updateBookingFee, toggleActive } =
    useServices();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<AdminService | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const openCreate = () => {
    setEditingService(null);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (service: AdminService) => {
    setEditingService(service);
    setFormError(null);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingService(null);
    setFormError(null);
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleSubmit = async (form: ServiceFormData) => {
    setFormError(null);
    let err: string | null;
    if (editingService) {
      err = await updateService(editingService.id, form);
      if (!err) {
        handleClose();
        showSuccess('Servicio actualizado correctamente');
      }
    } else {
      err = await createService(form);
      if (!err) {
        handleClose();
        showSuccess('Servicio creado correctamente');
      }
    }
    if (err) setFormError(err);
  };

  const activeCount = services.filter((s) => s.active).length;

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: theme.colors.bgBase }}>
      <AdminSidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <AdminTopBar title="Servicios" subtitle="Gestión de servicios" />

        <main className="flex-1 overflow-y-auto px-6 py-8">
          <PageHeader
            title="Servicios"
            subtitle={`${services.length} servicios · ${activeCount} activos`}
            action={{
              label: 'Nuevo servicio',
              icon: 'ri-add-line',
              onClick: openCreate,
            }}
          />

          {/* Error banner */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
              style={{
                backgroundColor: theme.colors.dangerMuted,
                borderColor: 'rgba(248,113,113,0.2)',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-400 text-base" />
              </div>
              <p className="text-red-400 text-sm flex-1">{error}</p>
              <button
                onClick={refetch}
                className="text-red-400 hover:text-red-300 text-xs underline cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <RefreshCw size={11} />
                Reintentar
              </button>
            </motion.div>
          )}

          {/* Success toast */}
          {successMsg && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-3 rounded-xl px-5 py-4 mb-6 border"
              style={{
                backgroundColor: 'rgba(52,211,153,0.08)',
                borderColor: 'rgba(52,211,153,0.2)',
              }}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-check-line text-emerald-400 text-base" />
              </div>
              <p className="text-emerald-400 text-sm">{successMsg}</p>
            </motion.div>
          )}

          {/* Stats row */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-3 gap-4 mb-6"
          >
            {[
              { label: 'Total servicios', value: services.length, color: 'text-white' },
              { label: 'Activos', value: activeCount, color: 'text-emerald-400' },
              { label: 'Inactivos', value: services.length - activeCount, color: theme.colors.textMuted },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border px-5 py-4"
                style={{
                  backgroundColor: theme.colors.bgSurface,
                  borderColor: theme.colors.border,
                }}
              >
                <p className="text-xs mb-1" style={{ color: theme.colors.textDisabled }}>
                  {stat.label}
                </p>
                <p
                  className={`text-2xl font-bold ${typeof stat.color === 'string' && stat.color.startsWith('text-') ? stat.color : ''}`}
                  style={typeof stat.color === 'string' && !stat.color.startsWith('text-') ? { color: stat.color } : {}}
                >
                  {stat.value}
                </p>
              </div>
            ))}
          </motion.div>

          {/* Booking Fee Section */}
          <BookingFeeSection
            services={services}
            loading={loading}
            onUpdateFee={updateBookingFee}
          />

          {/* Table */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-sm font-semibold text-white"
                style={{ fontFamily: theme.fonts.display }}
              >
                Todos los servicios
              </h3>
              <button
                onClick={refetch}
                disabled={loading}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                style={{
                  color: theme.colors.textMuted,
                  borderColor: theme.colors.border,
                  backgroundColor: 'transparent',
                }}
              >
                <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
                Actualizar
              </button>
            </div>

            <ServicesTable
              services={services}
              loading={loading}
              onEdit={openEdit}
              onToggleActive={toggleActive}
            />
          </motion.div>

          {/* Floating create button (mobile) */}
          <button
            onClick={openCreate}
            className="fixed bottom-6 right-6 md:hidden w-14 h-14 rounded-full flex items-center justify-center cursor-pointer whitespace-nowrap z-40"
            style={{ backgroundColor: theme.colors.primary }}
          >
            <Plus size={22} color="#0a0a0a" />
          </button>
        </main>
      </div>

      {/* Form modal */}
      <ServiceFormModal
        open={modalOpen}
        editingService={editingService}
        saving={saving}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />

      {formError && !modalOpen && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-red-500/90 text-white text-sm px-5 py-3 rounded-xl z-50">
          {formError}
        </div>
      )}
    </div>
  );
}
